import React, { useState } from 'react';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { useFM } from '@/context/FMContext';
import { useFMKpis } from '@/hooks/useFMKpis';
import { getStatusColors, formatCurrency } from '@/lib/fmUtils';
import KpiCard from '@/components/fm/KpiCard';
import PullToRefresh from 'react-simple-pull-to-refresh';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';

/**
 * Determine individual KPI status from progress_pct.
 * >= 100: GREEN, >= 70: YELLOW, < 70: RED
 */
function getKpiStatus(progressPct) {
  if (progressPct == null) return null;
  if (progressPct >= 100) return 'GREEN';
  if (progressPct >= 70) return 'YELLOW';
  return 'RED';
}

/**
 * Format a KPI value for the drill-down detail view.
 */
function formatKpiValue(value, format) {
  if (value == null) return '--';
  if (format === 'currency') return formatCurrency(value);
  if (format === 'percentage') return `${Math.round(value)}%`;
  return String(Math.round(value));
}

/**
 * FMKpiDashboard - 11 KPI scrolling cards with overall status badge and drill-down drawer.
 * Mobile-first, designed for one-hand use on the gym floor.
 */
export default function FMKpiDashboard() {
  const { selectedGymId, selectedGym } = useFM();
  const { kpis, status, statusReason, loading, error, refetch } = useFMKpis(selectedGymId);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleCardTap = (kpi) => {
    setSelectedKpi(kpi);
    setDrawerOpen(true);
  };

  const handleDrawerClose = (open) => {
    if (!open) {
      setDrawerOpen(false);
      // Delay clearing kpi to avoid flash during close animation
      setTimeout(() => setSelectedKpi(null), 300);
    }
  };

  const handleRefresh = async () => {
    await refetch();
  };

  // Loading skeleton: 11 card shapes
  if (loading) {
    return (
      <div className="space-y-3">
        {/* Status badge skeleton */}
        <div className="rounded-xl p-4 bg-slate-100 animate-pulse">
          <div className="h-8 w-28 bg-slate-200 rounded mx-auto mb-2" />
          <div className="h-4 w-48 bg-slate-200 rounded mx-auto" />
        </div>
        {/* Card skeletons */}
        {Array.from({ length: 11 }, (_, i) => (
          <div key={i} className="rounded-xl p-5 bg-white border shadow-sm animate-pulse">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-200" />
              <div className="h-4 w-32 bg-slate-200 rounded" />
            </div>
            <div className="h-8 w-24 bg-slate-200 rounded mb-3" />
            <div className="h-2 w-full bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && kpis.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500 mb-4">{error}</p>
        <button
          onClick={refetch}
          className="text-sm text-blue-500 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  // Overall status badge colors
  const overallColors = status ? getStatusColors(status) : null;

  return (
    <div className="space-y-3">
      {/* Overall Status Badge */}
      {status && (
        <div className={`rounded-xl p-4 text-center ${overallColors?.bg || 'bg-slate-100'} ${overallColors?.border || 'border-slate-200'} border`}>
          <div className={`text-2xl font-bold ${overallColors?.text || 'text-slate-800'}`}>
            {status}
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {statusReason || `${selectedGym?.name || selectedGym?.gym_name || 'Gym'} overall status`}
          </div>
        </div>
      )}

      {/* KPI Cards - Pull to Refresh wrapper */}
      <PullToRefresh
        onRefresh={handleRefresh}
        pullingContent={
          <div className="text-center text-sm text-slate-400 py-2">Pull down to refresh</div>
        }
      >
        <div className="space-y-3">
          {kpis.map((kpi) => {
            const kpiStatus = getKpiStatus(kpi.progress_pct);
            return (
              <KpiCard
                key={kpi.id || kpi.label}
                kpi={kpi}
                status={kpiStatus}
                onClick={() => handleCardTap(kpi)}
              />
            );
          })}

          {kpis.length === 0 && (
            <div className="text-center py-12">
              <p className="text-sm text-slate-400">No KPI data available for this gym.</p>
            </div>
          )}
        </div>
      </PullToRefresh>

      {/* Drill-down Drawer */}
      <Drawer open={drawerOpen} onOpenChange={handleDrawerClose} snapPoints={[0.4, 0.8]}>
        <DrawerContent className="max-h-[85vh]">
          {selectedKpi && (
            <div className="px-5 pb-6">
              <DrawerHeader className="px-0">
                <DrawerTitle className="text-left">{selectedKpi.label}</DrawerTitle>
                <DrawerDescription className="text-left">
                  KPI details and performance breakdown
                </DrawerDescription>
              </DrawerHeader>

              <div className="space-y-5 mt-2">
                {/* Current Value - Large */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Current Value</p>
                  <p className="text-4xl font-bold text-slate-900">
                    {formatKpiValue(selectedKpi.value, selectedKpi.format)}
                  </p>
                </div>

                {/* Target */}
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Target</p>
                    <p className="text-lg font-semibold text-slate-700">
                      {formatKpiValue(selectedKpi.target, selectedKpi.format)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Achievement</p>
                    <p className="text-lg font-semibold text-slate-700">
                      {selectedKpi.progress_pct != null ? `${Math.round(selectedKpi.progress_pct)}%` : '--'}
                    </p>
                  </div>
                </div>

                {/* Trend Indicator (placeholder) */}
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  {selectedKpi.progress_pct != null && selectedKpi.progress_pct >= 100 ? (
                    <>
                      <ArrowUp className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600 font-medium">On target</span>
                    </>
                  ) : selectedKpi.progress_pct != null && selectedKpi.progress_pct >= 70 ? (
                    <>
                      <Minus className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-yellow-600 font-medium">Needs attention</span>
                    </>
                  ) : (
                    <>
                      <ArrowDown className="w-4 h-4 text-red-500" />
                      <span className="text-sm text-red-600 font-medium">Below target</span>
                    </>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">vs target</span>
                </div>

                {/* Trainer Breakdown (if available) */}
                {selectedKpi.breakdown && selectedKpi.breakdown.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Trainer Breakdown</p>
                    <div className="space-y-2">
                      {selectedKpi.breakdown.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
                          <span className="text-sm font-medium text-slate-700">{item.name || `Trainer ${idx + 1}`}</span>
                          <span className="text-sm text-slate-600">
                            {formatKpiValue(item.value, selectedKpi.format)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* View Details link (future deep drill-down) */}
                <div className="pt-2">
                  <button
                    className="w-full text-center text-sm text-blue-500 font-medium py-2.5 rounded-lg border border-blue-100 bg-blue-50 active:bg-blue-100 transition-colors"
                    onClick={() => {
                      // Future: navigate to detailed KPI page
                      setDrawerOpen(false);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
